import { useContext, useEffect, useState } from 'preact/hooks';
import { useField, useFormikContext } from 'formik';

import { ChevronIcon } from '../ecardsPreact.icons/ChevronIcon';
import { Directions } from '../ecardsPreact.constants/directions';
import { Steps } from '../ecardsPreact.constants/steps';
import { ECardGlobalState } from './ECardGlobalState';
import { FieldError } from './FieldError';
import FocusOnFirstError from '../ecardsPreact.validation/FocusOnFirstError';
import { GADatalayerEcardCheckout } from '../ecardsPreact.common/App';

export default function ECardStepControls(props) {
    const { className, errors, isSubmitting, touched, backUrl } = props;

    const { values, setFieldTouched, submitForm } = useFormikContext();
    const [skipDonation, skipDonationMeta, skipDonationHelpers] = useField('SkipDonation');
    const [controlsMessage, controlsMessageMeta, controlsMessageHelpers] = useField('ControlsMessage');
    const [donationAmount, donationAmountMeta, donationAmountHelpers] = useField('EcardMessageDetails.DonationAmount');
    const [donationVisible, donationVisibleMeta, donationVisibleHelpers] = useField('EcardMessageDetails.DonationVisible');
    const { currentStep, isScheduled, isValidCsv, recipientsCsvError, maxRecipientsVisible, isGtmCard, ecardCategory, ecardDesignName } = useContext(ECardGlobalState);
    const [skipClicked, setSkipClicked] = useState(false);

    const currentStepVal = currentStep.get;
    const isScheduledVal = isScheduled.get;
    const isValidCsvVal = isValidCsv.get;
    const nextCopy = setNextCopy();

    const hideBack = currentStepVal === Steps.AddRecipientsOptions;
    const hideNext = currentStepVal === Steps.AddRecipientsOptions;
    const hideTextButton = currentStepVal !== Steps.AddRecipientsOptions && currentStepVal !== Steps.DonationSelect;

    const handleNext = (event) => {
        let newStep = currentStepVal;

        switch (currentStepVal) {
            case Steps.Personalise:
                event.preventDefault();
                const recipientsHaveErrors = errors.EcardMessageDetails?.Recipients?.length > 0 || false;
                const formIsTouched = Object.values(touched).length > 0;
                if (!formIsTouched || errors.EcardMessageDetails?.FromName || errors.EcardMessageDetails?.Message || recipientsHaveErrors) {
                    values.EcardMessageDetails?.Recipients?.forEach((r, i) => {
                        setFieldTouched(`EcardMessageDetails.Recipients.${i}.Name`, true, true);
                        setFieldTouched(`EcardMessageDetails.Recipients.${i}.Email`, true, true);
                    });
                    setFieldTouched('EcardMessageDetails.FromName', true, true);
                    setFieldTouched('EcardMessageDetails.Message', true, true);

                    if (recipientsHaveErrors) {
                        maxRecipientsVisible.set(-1); // Will display all recipient fields
                    }

                    FocusOnFirstError(errors);
                    return;
                } else {
                    newStep = Steps.Delivery;
                }
                break;

            case Steps.AddRecipientsManual:
                event.preventDefault();
                newStep = Steps.Personalise;
                break;

            case Steps.AddRecipientsCsv:
                event.preventDefault();
                if (isValidCsvVal) {
                    newStep = Steps.Personalise;
                } else {
                    if (recipientsCsvError.get?.length === 0) {
                        recipientsCsvError.set('Please upload a valid CSV that matches the example provided');
                    }
                    FocusOnFirstError(errors);
                    return;
                }
                break;

            case Steps.Delivery:
                if (isGtmCard === false) {
                    event.preventDefault();
                }
                if (
                    isScheduledVal !== undefined &&
                    (isScheduledVal === false ||
                        (touched.DeliveryDate && touched.DeliveryTime && errors.DeliveryDate === undefined && errors.DeliveryTime === undefined))
                ) {
                    if (isGtmCard === false) {
                        newStep = Steps.DonationSelect;
                    }
                } else {
                    if (isGtmCard === true) {
                        event.preventDefault();
                    }
                    setFieldTouched(`DeliveryDate`, true, true);
                    setFieldTouched(`DeliveryTime`, true, true);
                    FocusOnFirstError(errors);
                    return;
                }
                break;

            case Steps.DonationSelect:
                skipDonationHelpers.setValue(false, true);
                FocusOnFirstError(errors);
                break;
        }

        if (currentStepVal != newStep) {
            GADatalayerEcardCheckout(newStep, ecardCategory, isGtmCard === true ? values.EcardMessageDetails.DonationAmount.toString() : ecardDesignName);
        }

        currentStep.set(newStep);
    };

    const handleBack = () => {
        let newStep = currentStepVal;

        switch (currentStepVal) {
            case Steps.AddRecipientsOptions:
                newStep = Steps.Personalise;
                break;

            case Steps.AddRecipientsManual:
                newStep = Steps.AddRecipientsOptions;
                break;

            case Steps.AddRecipientsCsv:
                newStep = Steps.AddRecipientsOptions;
                break;

            case Steps.Delivery:
                newStep = Steps.Personalise;
                break;

            case Steps.DonationSelect:
                newStep = Steps.Delivery;
                break;
        }
        GADatalayerEcardCheckout(newStep, ecardCategory, isGtmCard === true ? values.EcardMessageDetails.DonationAmount.toString() : ecardDesignName);
        currentStep.set(newStep);
    };

    useEffect(() => {
        if (skipDonation.value === true && donationAmount.value === 0) {
            submitForm();
        }
    }, [skipClicked, skipDonation.value, donationAmount.value]);

    const handleSkipDonation = () => {
        switch (currentStepVal) {
            case Steps.DonationSelect:
                donationAmountHelpers.setValue(0);
                donationVisibleHelpers.setValue(false);
                skipDonationHelpers.setValue(true);
                setSkipClicked(!skipClicked);
                break;
        }
    };

    function setNextCopy() {
        switch (currentStepVal) {
            case Steps.AddRecipientsManual:
            case Steps.AddRecipientsCsv:
                return 'Add';
            default:
                return 'Next';
        }
    }

    return (
        <>
            <div class={`ecard-form-controls ${className}`}>
                {currentStepVal === Steps.Personalise ? (
                    // Goes back to relevant card category listing page
                    <a class={`ecard-form-controls__prev arrow-button${hideBack ? ' js-hide' : ''}`} href={backUrl ?? '#'}>
                        <ChevronIcon class="arrow-button__icon" width="24" height="24" direction={Directions.Left} />
                        <span>Back</span>
                    </a>
                ) : (
                    <button
                        class={`ecard-form-controls__prev arrow-button${hideBack ? ' js-hide' : ''}`}
                        type="button"
                        onClick={handleBack}
                        disabled={isSubmitting || hideBack}
                    >
                        <ChevronIcon class="arrow-button__icon" width="24" height="24" direction={Directions.Left} />
                        <span>Back</span>
                    </button>
                )}

                {currentStepVal === Steps.AddRecipientsOptions && (
                    <button
                        class={`ecard-form-controls__cancel text-button${hideTextButton ? ' js-hide' : ''}`}
                        type="button"
                        onClick={handleBack}
                        disabled={isSubmitting || hideTextButton}
                    >
                        Cancel
                    </button>
                )}

                <button
                    class={`ecard-form-controls__next button${hideNext ? ' js-hide' : ''}`}
                    type={currentStepVal === Steps.DonationSelect || (isGtmCard && currentStepVal === Steps.Delivery) ? 'submit' : 'button'}
                    onClick={handleNext}
                    disabled={isSubmitting || hideNext}
                >
                    {nextCopy}
                </button>

                {currentStepVal === Steps.DonationSelect && (
                    <div class={`ecard-form-controls__skip ecard-form-controls__skip`}>
                        <button
                            class={`text-button${hideTextButton ? ' js-hide' : ''}`}
                            type="button"
                            onClick={handleSkipDonation}
                            disabled={isSubmitting || hideTextButton}
                        >
                            Skip donation
                        </button>
                    </div>
                )}
            </div>
            <FieldError {...controlsMessageMeta} className="field-error--controls" />
        </>
    );
}
